import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';

const Group = observer(() => {
	const { assignUsersStore } = useStores();
	const { groups, properties, status } = assignUsersStore;
	const tenantId = useAssignmentTenantId();

	const {
		draft: {
			customSetup: { selectedGroups, selectedGroupPropertyIds }
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');

	// Groups are reached through properties, so scoping the property list to the
	// tenant scopes the groups with it.
	useEffect(() => {
		if (
			!properties.length ||
			assignUsersStore.propertiesTenantId !== tenantId
		) {
			assignUsersStore.fetchProperties(tenantId);
		}
	}, [tenantId]);

	useEffect(() => {
		assignUsersStore.fetchGroupsByProperty(selectedGroupPropertyIds);
	}, [selectedGroupPropertyIds]);

	const propertyOptions = properties.map((p) => ({ id: p.id, label: p.name }));

	const filtered = search.trim()
		? groups.filter((g) =>
				g.name.toLowerCase().includes(search.toLowerCase())
		  )
		: groups;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const group = groups.find((g) => g.id === numId);
		if (!group) return;
		if (checked !== selectedGroups.some((g) => g.id === numId)) {
			toggleAssignUsersSelection('selectedGroups', {
				id: group.id,
				name: group.name,
				label: group.label
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedGroups.length === filtered.length) {
			setAssignUsersSelection('selectedGroups', []);
		} else {
			setAssignUsersSelection(
				'selectedGroups',
				filtered.map((g) => ({
					id: g.id,
					name: g.name,
					label: g.label
				}))
			);
		}
	};

	const isAllSelected =
		filtered.length > 0 &&
		filtered.every((g) => selectedGroups.some((sg) => sg.id === g.id));
	const isLoading = status.fetchGroups === Status.Pending;

	return (
		<div id="lms-ca-assign-users-group">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div
						id="lms-ca-au-group-property-filter"
						className={styles.search}
					>
						<div className={styles.selectProp}>Select Property</div>
						<MultiSelectDropdown
							options={propertyOptions}
							placeholder="Select option"
							onChange={(ids) => {
								setAssignUsersPropertyFilter(
									'selectedGroupPropertyIds',
									ids
								);
							}}
							defaultSelectedIds={selectedGroupPropertyIds}
						/>
					</div>
					<Input.Search
						id="lms-ca-au-group-search"
						className={styles.search}
						placeholder="Search"
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title="Select groups"
					astrix={true}
				/>
				{filtered.length > 1 && (
					<div
						id="lms-ca-au-group-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isAllSelected ? 'Deselect all' : 'Select all'}
					</div>
				)}
			</div>
			<div id="lms-ca-au-group-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{!isLoading && selectedGroupPropertyIds.length === 0 && (
					<div className={styles.empty}>
						Select a property to see available groups.
					</div>
				)}
				{filtered.map((group) => {
					const isChecked = selectedGroups.some(
						(g) => g.id === group.id
					);
					return (
						<CheckBoxCard
							key={group.id}
							id={group.id}
							htmlId={`lms-ca-au-group-${group.id}`}
							icon={
								<Icon
									type={
										isChecked ? 'groupOptFilled' : 'groupOptIcon'
									}
								/>
							}
							title={group.name}
							desc={group.label}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default Group;
